/**
 * ODAX (Outcome-Driven Ads Experiences) mappings for Meta/Facebook ads.
 *
 * Labels match Meta Ads Manager UI exactly (as of 2026).
 * API field names are documented in comments.
 */

export type CampaignObjective =
    | 'AWARENESS'
    | 'TRAFFIC'
    | 'ENGAGEMENT'
    | 'LEADS'
    | 'SALES'
    | 'APP_PROMOTION';

/**
 * Normalize FB ODAX v2 objective names (OUTCOME_SALES, OUTCOME_LEADS, etc.)
 * to the base names used in our PERFORMANCE_GOALS map.
 */
export function normalizeObjective(objective: string): CampaignObjective {
    const map: Record<string, CampaignObjective> = {
        OUTCOME_AWARENESS: 'AWARENESS',
        OUTCOME_TRAFFIC: 'TRAFFIC',
        OUTCOME_ENGAGEMENT: 'ENGAGEMENT',
        OUTCOME_LEADS: 'LEADS',
        OUTCOME_SALES: 'SALES',
        OUTCOME_APP_PROMOTION: 'APP_PROMOTION',
    };
    return map[objective] || objective as CampaignObjective;
}

export interface PerformanceGoal {
    /** API value for `optimization_goal` */
    value: string;
    /** Label as shown in Meta Ads Manager */
    label: string;
    /** Whether this goal requires a dataset (pixel) + conversion event */
    requiresDataset?: boolean;
}

/**
 * "Performance goal" dropdown options per campaign objective.
 * Maps to API field: `optimization_goal`
 */
export const PERFORMANCE_GOALS: Record<CampaignObjective, PerformanceGoal[]> = {
    AWARENESS: [
        { value: 'REACH', label: 'Maximize daily unique reach' },
        { value: 'IMPRESSIONS', label: 'Maximize number of impressions' },
        { value: 'AD_RECALL_LIFT', label: 'Maximize ad recall lift' },
        { value: 'THRUPLAY', label: 'Maximize ThruPlay video views' },
    ],
    TRAFFIC: [
        { value: 'LANDING_PAGE_VIEWS', label: 'Maximize number of landing page views' },
        { value: 'LINK_CLICKS', label: 'Maximize number of link clicks' },
        { value: 'REACH', label: 'Maximize daily unique reach' },
        { value: 'IMPRESSIONS', label: 'Maximize number of impressions' },
    ],
    ENGAGEMENT: [
        { value: 'POST_ENGAGEMENT', label: 'Maximize post engagement' },
        { value: 'PAGE_LIKES', label: 'Maximize page likes' },
        { value: 'EVENT_RESPONSES', label: 'Maximize event responses' },
        { value: 'THRUPLAY', label: 'Maximize ThruPlay video views' },
        { value: 'CONVERSATIONS', label: 'Maximize conversations' },
    ],
    LEADS: [
        { value: 'LEAD_GENERATION', label: 'Maximize number of leads (instant form)' },
        { value: 'QUALITY_LEAD', label: 'Maximize number of quality leads' },
        { value: 'OFFSITE_CONVERSIONS', label: 'Maximize number of conversion leads', requiresDataset: true },
        { value: 'LINK_CLICKS', label: 'Maximize number of link clicks' },
    ],
    SALES: [
        { value: 'OFFSITE_CONVERSIONS', label: 'Maximize number of conversions', requiresDataset: true },
        { value: 'VALUE', label: 'Maximize value of conversions', requiresDataset: true },
        { value: 'LANDING_PAGE_VIEWS', label: 'Maximize number of landing page views' },
        { value: 'LINK_CLICKS', label: 'Maximize number of link clicks' },
        { value: 'REACH', label: 'Maximize daily unique reach' },
        { value: 'IMPRESSIONS', label: 'Maximize number of impressions' },
    ],
    APP_PROMOTION: [
        { value: 'APP_INSTALLS', label: 'Maximize number of app installs' },
        { value: 'OFFSITE_CONVERSIONS', label: 'Maximize number of app events', requiresDataset: true },
        { value: 'LINK_CLICKS', label: 'Maximize number of link clicks' },
    ],
};

/**
 * Conversion location options.
 * Maps to API field: `destination_type`
 */
export const CONVERSION_LOCATIONS = [
    { value: 'WEBSITE', label: 'Website' },
    { value: 'APP', label: 'App' },
    { value: 'MESSAGING', label: 'Messaging apps' },
    { value: 'CALLS', label: 'Calls' },
] as const;

/**
 * Conversion event types for datasets (pixels).
 * Maps to API field: `promoted_object.custom_event_type`
 */
export const CONVERSION_EVENTS = [
    { value: 'PURCHASE', label: 'Purchase' },
    { value: 'ADD_TO_CART', label: 'Add to Cart' },
    { value: 'INITIATE_CHECKOUT', label: 'Initiate Checkout' },
    { value: 'ADD_PAYMENT_INFO', label: 'Add Payment Info' },
    { value: 'LEAD', label: 'Lead' },
    { value: 'COMPLETE_REGISTRATION', label: 'Complete Registration' },
    { value: 'SUBSCRIBE', label: 'Subscribe' },
    { value: 'ADD_TO_WISHLIST', label: 'Add to Wishlist' },
    { value: 'VIEW_CONTENT', label: 'View Content' },
    { value: 'SEARCH', label: 'Search' },
    { value: 'CONTACT', label: 'Contact' },
    { value: 'START_TRIAL', label: 'Start Trial' },
] as const;

/**
 * Attribution settings.
 * Maps to API field: `attribution_spec`
 */
export const ATTRIBUTION_SETTINGS = [
    { label: '7-day click, 1-day view (default)', spec: [
        { event_type: 'CLICK_THROUGH', window_days: 7 },
        { event_type: 'VIEW_THROUGH', window_days: 1 },
    ] },
    { label: '1-day click, 1-day view', spec: [
        { event_type: 'CLICK_THROUGH', window_days: 1 },
        { event_type: 'VIEW_THROUGH', window_days: 1 },
    ] },
    { label: '7-day click only', spec: [
        { event_type: 'CLICK_THROUGH', window_days: 7 },
    ] },
    { label: '1-day click only', spec: [
        { event_type: 'CLICK_THROUGH', window_days: 1 },
    ] },
] as const;

/** Common languages for targeting (ISO 639-1 locale codes used by FB) */
export const LANGUAGES = [
    { code: 6, label: 'English (all)' },
    { code: 23, label: 'Spanish' },
    { code: 10, label: 'French' },
    { code: 7, label: 'German' },
    { code: 16, label: 'Portuguese' },
    { code: 15, label: 'Italian' },
    { code: 5, label: 'Dutch' },
    { code: 25, label: 'Russian' },
    { code: 47, label: 'Arabic' },
    { code: 45, label: 'Hebrew' },
    { code: 24, label: 'Japanese' },
    { code: 20, label: 'Korean' },
    { code: 44, label: 'Chinese (Simplified)' },
    { code: 22, label: 'Hindi' },
    { code: 28, label: 'Turkish' },
    { code: 17, label: 'Polish' },
    { code: 27, label: 'Thai' },
    { code: 29, label: 'Vietnamese' },
    { code: 26, label: 'Indonesian' },
] as const;

/** Bid strategies */
export const BID_STRATEGIES = [
    { value: 'LOWEST_COST_WITHOUT_CAP', label: 'Lowest cost (automatic)', description: 'Meta optimizes for lowest cost automatically' },
    { value: 'LOWEST_COST_WITH_BID_CAP', label: 'Bid cap', description: 'Set a maximum bid amount' },
    { value: 'COST_CAP', label: 'Cost cap', description: 'Control your average cost per result' },
] as const;
